import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import * as Models from 'Models/Entities';

export interface IParkingPricingTierEntityAttributes extends IModelAttributes {
	name: string;
	parkingLocationId: string;
	parkingLocation: Models.ParkingLocationEntity | Models.IParkingLocationEntityAttributes;
	parkingVehicleTypeId?: string;
	parkingVehicleType?: Models.ParkingVehicleTypeEntity | Models.IParkingVehicleTypeEntityAttributes;
	minDays?: number;
	maxDays?: number;
	dailyRate?: number;
	flatRate?: number;
	effectiveFrom?: string;
	effectiveTo?: string;
	priority: number;
	disabled?: boolean;
}

@entity('ParkingPricingTierEntity', 'Parking Pricing Tier')
export default class ParkingPricingTierEntity extends Model implements IParkingPricingTierEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<ParkingPricingTierEntity, string>()
	public name: string;

	@observable
	@attribute()
	public parkingLocationId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public parkingLocation: Models.ParkingLocationEntity;

	@observable
	@attribute()
	public parkingVehicleTypeId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public parkingVehicleType?: Models.ParkingVehicleTypeEntity;

	@observable
	@attribute<ParkingPricingTierEntity, number>()
	public minDays?: number;

	@observable
	@attribute<ParkingPricingTierEntity, number>()
	public maxDays?: number;

	@observable
	@attribute<ParkingPricingTierEntity, number>()
	public dailyRate?: number;

	@observable
	@attribute<ParkingPricingTierEntity, number>()
	public flatRate?: number;

	@observable
	@attribute<ParkingPricingTierEntity, string>()
	public effectiveFrom?: string;

	@observable
	@attribute<ParkingPricingTierEntity, string>()
	public effectiveTo?: string;

	@observable
	@attribute<ParkingPricingTierEntity, number>()
	public priority: number = 0;

	@observable
	@attribute<ParkingPricingTierEntity, boolean>()
	public disabled?: boolean;

	constructor(attributes?: Partial<IParkingPricingTierEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IParkingPricingTierEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (isNotNullOrUndefined(attributes.name)) {
				this.name = attributes.name;
			}
			if (isNotNullOrUndefined(attributes.parkingLocationId)) {
				this.parkingLocationId = attributes.parkingLocationId;
			}
			if (isNotNullOrUndefined(attributes.parkingLocation)) {
				if (attributes.parkingLocation === null) {
					this.parkingLocation = attributes.parkingLocation;
				} else if (attributes.parkingLocation instanceof Models.ParkingLocationEntity) {
					this.parkingLocation = attributes.parkingLocation;
					this.parkingLocationId = attributes.parkingLocation.id;
				} else {
					this.parkingLocation = new Models.ParkingLocationEntity(attributes.parkingLocation);
					this.parkingLocationId = this.parkingLocation.id;
				}
			}
			if (isNotNullOrUndefined(attributes.parkingVehicleTypeId)) {
				this.parkingVehicleTypeId = attributes.parkingVehicleTypeId;
			}
			if (isNotNullOrUndefined(attributes.parkingVehicleType)) {
				if (attributes.parkingVehicleType === null) {
					this.parkingVehicleType = attributes.parkingVehicleType;
				} else if (attributes.parkingVehicleType instanceof Models.ParkingVehicleTypeEntity) {
					this.parkingVehicleType = attributes.parkingVehicleType;
					this.parkingVehicleTypeId = attributes.parkingVehicleType.id;
				} else {
					this.parkingVehicleType = new Models.ParkingVehicleTypeEntity(attributes.parkingVehicleType);
					this.parkingVehicleTypeId = this.parkingVehicleType.id;
				}
			}
			if (isNotNullOrUndefined(attributes.minDays)) {
				this.minDays = attributes.minDays;
			}
			if (isNotNullOrUndefined(attributes.maxDays)) {
				this.maxDays = attributes.maxDays;
			}
			if (isNotNullOrUndefined(attributes.dailyRate)) {
				this.dailyRate = attributes.dailyRate;
			}
			if (isNotNullOrUndefined(attributes.flatRate)) {
				this.flatRate = attributes.flatRate;
			}
			if (isNotNullOrUndefined(attributes.effectiveFrom)) {
				this.effectiveFrom = attributes.effectiveFrom;
			}
			if (isNotNullOrUndefined(attributes.effectiveTo)) {
				this.effectiveTo = attributes.effectiveTo;
			}
			if (isNotNullOrUndefined(attributes.priority)) {
				this.priority = attributes.priority;
			}
			if (isNotNullOrUndefined(attributes.disabled)) {
				this.disabled = attributes.disabled;
			}
		}
	}
}
